import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import AutoCompleteDropdown from "Components/common/AutoCompleteDropdown";
import FilterDropdown from "../../Triggers/AddEditTrigger/FilterDropdown";
import DeleteRow from "../../Triggers/AddEditTrigger/DeleteRow";
import AddItem from "../../Triggers/AddEditTrigger/AddItem";
import ConditionValuesInput from "./ConditionValuesInput";
import { salesforceConnectedData } from "./SalesforceConnectedData";
import { syncConfirmationPopupState } from "../SyncConfirmationPopup";

const defaultConditionsObject = {
  fieldName: "",
  operator: "IN",
  value: "",
  values: [],
  name: ""
};

// Legacy operators map onto the new ones for display; new conditions save IN / NOT_IN / CONTAINS
const OPERATOR_DISPLAY = {
  EQUALS:
    "<span class='conditionSymbol'>&equals;</span><span class='conditionLabel'>Is any of</span>",
  IN: "<span class='conditionSymbol'>&equals;</span><span class='conditionLabel'>Is any of</span>",
  NOT_EQUALS:
    "<span class='conditionSymbol'>&ne;</span><span class='conditionLabel'>Is none of</span>",
  NOT_IN:
    "<span class='conditionSymbol'>&ne;</span><span class='conditionLabel'>Is none of</span>",
  CONTAINS:
    "<span class='conditionSymbol'>&ni;</span><span class='conditionLabel'>Contains</span>",
  GREATER_THAN:
    "<span class='conditionSymbol'>&gt;</span><span class='conditionLabel'>Greater than</span>",
  LESS_THAN:
    "<span class='conditionSymbol'>&lt;</span><span class='conditionLabel'>Less than</span>",
  AFTER:
    "<span class='conditionSymbol'>&gt;</span><span class='conditionLabel'>After</span>",
  BEFORE:
    "<span class='conditionSymbol'>&lt;</span><span class='conditionLabel'>Before</span>",
  LAST_N_DAYS:
    "<span class='conditionSymbol'>&#8635;</span><span class='conditionLabel'>In last N days</span>",
  BETWEEN:
    "<span class='conditionSymbol'>&harr;</span><span class='conditionLabel'>Between</span>"
};

const NUMBER_TYPES = ["int", "double", "currency", "percent", "long"];
const DATE_TYPES = ["date", "datetime"];

const normalizeFieldType = dataType => {
  const type = (dataType || "").toLowerCase();
  if (DATE_TYPES.includes(type)) return "DATE";
  if (NUMBER_TYPES.includes(type)) return "NUMBER";
  if (type === "boolean") return "BOOLEAN";
  return "STRING";
};

// single-value input modes per operator
const OPERATOR_INPUT_MODE = {
  GREATER_THAN: "number",
  LESS_THAN: "number",
  LAST_N_DAYS: "number",
  BEFORE: "date",
  AFTER: "date",
  BETWEEN: "daterange"
};

const SfImportConditions = observer(props => {
  const { fields = {}, type = "LEADS", config = {}, setConfig = () => {} } =
    props || {};
  const isLeads = type === "LEADS";

  const [crmOptionList, setCrmOptionList] = useState({});
  const [crmMappedConfig, setCrmMappedConfig] = useState({});
  const [crmFieldOptions, setCrmFieldOptions] = useState({});
  const [crmFieldTypes, setCrmFieldTypes] = useState({});
  const [activeDropdownIndex, setActiveDropdownIndex] = useState("");

  const conditionListLength = () => getConditions()?.length || 0;

  const getConditions = () => {
    return isLeads
      ? salesforceConnectedData?.sfLeadConditions
      : salesforceConnectedData?.sfContactConditions;
  };

  const setConditions = list => {
    isLeads
      ? salesforceConnectedData.setSfLeadConditions(list)
      : salesforceConnectedData.setSfContactConditions(list);
  };

  const matchType = isLeads
    ? salesforceConnectedData?.sfLeadMatchType
    : salesforceConnectedData?.sfContactMatchType;

  const setMatchType = value => {
    isLeads
      ? salesforceConnectedData.setSfLeadMatchType(value)
      : salesforceConnectedData.setSfContactMatchType(value);
    setConfig({ ...config, listChanged: true });
  };

  const constructCrmFields = () => {
    // Show the API name next to the label so fields like "Lead Status" (API: Status) are findable
    const fieldOptionsMap = {};
    const fieldTypeMap = {};
    // Compound SF fields can't be queried through the bulk import — keep them out of conditions
    const COMPOUND_TYPES = ["address", "location"];
    const updatedCrmOptions = fields?.fieldList
      ?.filter(
        item => !COMPOUND_TYPES.includes((item?.dataType || "").toLowerCase())
      )
      ?.map(item => {
      const label =
        item?.crmFieldLabel &&
        item?.crmField &&
        item?.crmFieldLabel !== item?.crmField
          ? `${item?.crmFieldLabel} (${item?.crmField})`
          : item?.crmFieldLabel || item?.crmField;
      if (item?.options?.length > 0) {
        fieldOptionsMap[item.crmField] = item.options;
      }
      if (item?.crmField) {
        fieldTypeMap[item.crmField] = normalizeFieldType(item?.dataType);
      }
      return {
        value: item?.crmField,
        name: label,
        dataType: item?.dataType
      };
    });
    setCrmFieldOptions(fieldOptionsMap);
    setCrmFieldTypes(fieldTypeMap);
    const mappedCrmField =
      updatedCrmOptions?.length > 0 &&
      updatedCrmOptions?.reduce(function (accumulator, item) {
        accumulator[item?.value] = item?.name;
        return accumulator;
      }, {});
    setCrmMappedConfig(mappedCrmField);
    return updatedCrmOptions;
  };

  const updateConditionAt = (index, updatedFields) => {
    const tempList = [...getConditions()];
    tempList[index] = { ...tempList[index], ...updatedFields };
    setConditions(tempList);
  };

  const handleFieldChange = (item, index) => {
    const indexValue = Number(index?.split("-")?.[1]);
    const rowIndex = indexValue - 1;
    const current = getConditions()?.[rowIndex] || {};
    const fieldType = getFieldType(item?.value);
    const validOperators = getValidOperators(item?.value);
    const updated = { fieldName: item?.value, fieldType };
    if (
      item?.value === "OwnerId" &&
      !salesforceConnectedData?.sfOwnersList?.length
    ) {
      salesforceConnectedData.getAllOwners();
    }
    if (!validOperators.includes(current?.operator)) {
      updated.operator = validOperators[0];
      updated.values = [];
      updated.value = "";
    }
    updateConditionAt(rowIndex, updated);
    setConfig({ ...config, listChanged: true });
  };

  const handleOperatorChange = (referenceId, item) => {
    const splitArr = activeDropdownIndex?.split("-") || [];
    const rowIndex = Number(splitArr?.[1]);
    if (!isNaN(rowIndex)) {
      const current = getConditions()?.[rowIndex] || {};
      const updated = { operator: item?.subKey };
      if (OPERATOR_INPUT_MODE[item?.subKey]) {
        // fixed-arity operators: between keeps two values, the rest keep one
        const keep = item?.subKey === "BETWEEN" ? 2 : 1;
        const kept = (current?.values || []).slice(0, keep);
        updated.values = kept;
        updated.value = kept.join(",");
      }
      updateConditionAt(rowIndex, updated);
    }
    setActiveDropdownIndex("");
  };

  const handleValuesChange = (index, updatedValues) => {
    // keep legacy `value` in sync (comma-joined) for backward compatibility
    updateConditionAt(index, {
      values: updatedValues,
      value: updatedValues.join(",")
    });
  };

  const handleDelete = (_, index) => {
    const tempList = [...getConditions()];
    tempList.splice(index, 1);
    const updatedFieldName =
      tempList?.length > 0 ? tempList?.[index - 1]?.fieldName : "All Contacts";
    syncConfirmationPopupState.setHasUpdate(true);
    syncConfirmationPopupState.setCriteria(updatedFieldName);
    setConditions(tempList);
    setConfig({ ...config, listChanged: true });
  };

  const addNewConditions = () => {
    setConditions([...getConditions(), { ...defaultConditionsObject }]);
  };

  const getFieldType = fieldName => crmFieldTypes?.[fieldName] || "STRING";

  const getValueOptions = fieldName => {
    if (fieldName === "OwnerId") {
      const owners = salesforceConnectedData?.sfOwnersList;
      return Array.isArray(owners)
        ? owners.map(owner => ({
            value: owner?.id,
            label: owner?.name || owner?.email || owner?.id
          }))
        : [];
    }
    if (getFieldType(fieldName) === "BOOLEAN") {
      return [
        { value: "true", label: "True" },
        { value: "false", label: "False" }
      ];
    }
    return crmFieldOptions?.[fieldName] || [];
  };

  const getValidOperators = fieldName => {
    const fieldType = getFieldType(fieldName);
    if (fieldType === "DATE") {
      return ["AFTER", "BEFORE", "BETWEEN", "LAST_N_DAYS"];
    }
    if (fieldType === "NUMBER") {
      return ["IN", "NOT_IN", "GREATER_THAN", "LESS_THAN"];
    }
    if (fieldType === "BOOLEAN" || getValueOptions(fieldName)?.length > 0) {
      return ["IN", "NOT_IN"];
    }
    return ["IN", "NOT_IN", "CONTAINS"];
  };

  const getOperatorOptions = fieldName => {
    return getValidOperators(fieldName).map(operatorKey => ({
      value: OPERATOR_DISPLAY[operatorKey],
      subKey: operatorKey,
      key: "sfConditionOperator",
      method: handleOperatorChange
    }));
  };

  const getDisplayValues = item => {
    if (item?.values?.length > 0) {
      return [...item.values];
    }
    if (item?.value) {
      return item.value
        .split(",")
        .map(value => value.trim())
        .filter(Boolean);
    }
    return [];
  };

  useEffect(() => {
    setCrmOptionList(constructCrmFields());
  }, [fields]);

  useEffect(() => {
    const needsOwners = getConditions()?.some(
      item => item?.fieldName === "OwnerId"
    );
    if (needsOwners && !salesforceConnectedData?.sfOwnersList?.length) {
      salesforceConnectedData.getAllOwners();
    }
  }, [conditionListLength()]);

  const conditionList = getConditions();
  const currentMatchType = (matchType || "AND").toUpperCase();

  return (
    <div className="sfImportConditions">
      <div className="conditionsPanel">
        <div className="matchTypeSelect">
          {["AND", "OR"].map(item => (
            <button
              type="button"
              key={`matchType-${item}`}
              className={`matchOption ${
                currentMatchType === item ? "active" : ""
              }`}
              onClick={() => setMatchType(item)}
              title={
                item === "AND"
                  ? "Import leads matching all conditions"
                  : "Import leads matching any condition"
              }
            >
              {item}
            </button>
          ))}
        </div>
        <div className="railRows">
          {conditionList?.length > 0 &&
            conditionList.map((item, index) => {
              const { fieldName = "", operator = "IN" } = item || {};
              return (
                <div className="conditionRow" key={`sfCondition-${index + 1}`}>
                  <span className="rowMarker" />
                  <div className="fieldSegment">
                    <AutoCompleteDropdown
                      optionList={crmOptionList}
                      defaultValue={crmMappedConfig?.[fieldName]}
                      cbk={handleFieldChange}
                      index={`active-${index + 1}`}
                      loader={!crmOptionList}
                      customTitle="Select"
                    />
                  </div>
                  <div className="operatorSegment">
                    <FilterDropdown
                      options={getOperatorOptions(fieldName)}
                      showDropdown={activeDropdownIndex === `operator-${index}`}
                      setActiveDropdownIndex={setActiveDropdownIndex}
                      defaultValue={OPERATOR_DISPLAY?.[operator] || "Is any of"}
                      label=""
                      hasLabel={false}
                      referenceId="operator"
                      index={`operator-${index}`}
                    />
                  </div>
                  <div className="valuesSegment">
                    <ConditionValuesInput
                      values={getDisplayValues(item)}
                      changeCbk={updatedValues =>
                        handleValuesChange(index, updatedValues)
                      }
                      options={getValueOptions(fieldName)}
                      inputType={OPERATOR_INPUT_MODE?.[operator] || "chips"}
                      placeholder={
                        operator === "LAST_N_DAYS"
                          ? "Number of days"
                          : getValueOptions(fieldName)?.length > 0
                          ? "Choose values"
                          : "Type a value, press Enter"
                      }
                    />
                  </div>
                  <DeleteRow
                    childIndex={index}
                    className="deleteCondition"
                    deleteCbk={handleDelete}
                  />
                </div>
              );
            })}
        </div>
        <AddItem addCbk={addNewConditions} name="Add condition" />
      </div>
      <div className="conditionNotesSection">
        Add multiple values to match any of them. Matching ignores case.
      </div>
    </div>
  );
});

export { SfImportConditions };
export default SfImportConditions;
